import React, { useState, useEffect, useRef, forwardRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import styles from "../styles/DemoMap.module.css";

// Custom marker icons
const competitorIcon = L.icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const miloIcon = new L.Icon({
  iconUrl: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
  iconSize: [38, 38],
  iconAnchor: [19, 38],
  popupAnchor: [0, -38],
});

// Marker component that flies to a location when clicked.
const FlyToMarker = forwardRef(
  ({ position, icon, children, onMarkerClick }, ref) => {
    const map = useMap();

    const handleClick = () => {
      map.flyTo([42.34827, -71.0975], 17, { animate: true, duration: 0.5 });
      if (onMarkerClick) onMarkerClick();
    };

    return (
      <Marker
        position={position}
        icon={icon}
        eventHandlers={{ click: handleClick }}
        ref={ref}
      >
        <Popup>{children}</Popup>
      </Marker>
    );
  }
);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const DemoMap = ({ variant, setStep }) => {
  const isMilo = variant === "milo";
  const [rented, setRented] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const markerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
    const containerStyle =
    windowWidth < 525
      ? { position: "relative", width: "100%", height: "300px" }
      : {};

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!popupOpen && markerRef.current) {
        const marker = markerRef.current;
        const map = marker._map; // Get the map instance

        console.log("Simulating marker click...");
        marker.fire("click"); // Simulate user click

        await sleep(400); // Wait briefly

        if (marker.getPopup() && map) {
          console.log("Opening popup after delay...");
          marker.getPopup().openOn(map);
        }
        setPopupOpen(true);
      }
    }, 1);

    return () => clearTimeout(timer);
  }, [popupOpen]);

  const handleMarkerClick = () => {
    if (!mapReady || !mapRef.current) {
      setTimeout(handleMarkerClick, 200);
      return;
    }
    mapRef.current.flyTo([42.3440, -71.0975], 17, {
      animate: true,
      duration: 1.5,
    });
  };

  return (
    <div className={styles["demoMapContainer"]}>
      <MapContainer
        center={[42.3467, -71.0972]}
        zoom={17}
        className={styles["demoMap"]}
        whenCreated={(mapInstance) => {
          mapRef.current = mapInstance;
          setMapReady(true);
        }}
        dragging={true}
        zoomControl={true}
        scrollWheelZoom={true}
        doubleClickZoom={true}
        maxZoom={18}
        minZoom={16}
        maxBounds={[[42.3400, -71.1100], [42.3550, -71.0850]]}
        maxBoundsViscosity={1.0}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FlyToMarker
          position={[42.3464, -71.0975]}
          icon={isMilo ? miloIcon : competitorIcon}
          ref={markerRef}
          onMarkerClick={handleMarkerClick}
        >
          <Popup>
            <div className={styles["popupContent"]}>
              <img
                src="https://res.cloudinary.com/dknn0sm5e/image/upload/v1738701120/Fenway_Park_press_box_2019-07-27_pg4dmb.jpg"
                alt="Fenway Park"
                className={styles["popupImage"]}
              />
              <h4>🏠 Fenway Field Apartments</h4>
              <p>
                <strong>📍 Boston, MA</strong>
              </p>
              <p>🛏️ 2 Bed | 🛁 1.5 Bath</p>
              <p>💰 $2,500/month</p>
              {isMilo ? (
                rented ? (
                  <p className={styles["rentedMessage"]}>
                    🎉 You secured the unit!
                  </p>
                ) : (
                  <div className={styles["popupButtons"]}>
                    <button
                      className={styles["tourButton"]}
                      onClick={() => setStep(4)}
                    >
                      Schedule a Tour
                    </button>
                    <button
                      className={styles["leaseButton"]}
                      onClick={() => setStep(4)}
                    >
                      Sign Lease Instantly
                    </button>
                  </div>
                )
              ) : (
                <button
                  className={styles["contactLandlordButton"]}
                  onClick={() => setStep(2)}
                >
                  Contact Landlord
                </button>
              )}
            </div>
          </Popup>
        </FlyToMarker>
      </MapContainer>
    </div>
  );
};

export default DemoMap;
